/**
 * usePmRuleset — load the active ruleset and expose a save function.
 *
 * GET is open to any authenticated user.
 * PUT (save) is admin-only — the backend returns 403 for non-admins.
 *
 * The hook does not enforce the admin gate itself; the page component is
 * responsible for rendering the textarea read-only for non-admins (and for
 * hiding / disabling the Save button). If a non-admin somehow calls save(),
 * the ApiError propagates back as the returned `saveError`.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, pmRuleset as pmRulesetApi } from '@/helpers/ApiHelper';
import type { PmRuleset } from '@/types/pm_notification';

interface UsePmRulesetResult {
  ruleset: PmRuleset | null;
  isLoading: boolean;
  error: string | null;
  /** Save a new version of the ruleset body. Resolves with the new PmRuleset on success. */
  save: (body: string) => Promise<PmRuleset>;
  isSaving: boolean;
  saveError: string | null;
  /** Clear saveError (e.g. after the user edits the textarea again). */
  clearSaveError: () => void;
  refetch: () => void;
}

export function usePmRuleset(): UsePmRulesetResult {
  const [ruleset, setRuleset] = useState<PmRuleset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const data = await pmRulesetApi.get();
        if (!cancelled) setRuleset(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.detail : 'Failed to load ruleset');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const save = useCallback(async (body: string): Promise<PmRuleset> => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await pmRulesetApi.update(body);
      setRuleset(updated);
      return updated;
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Failed to save ruleset';
      setSaveError(msg);
      throw new Error(msg);
    } finally {
      setIsSaving(false);
    }
  }, []);

  return { ruleset, isLoading, error, save, isSaving, saveError, clearSaveError, refetch };
}
